import { useCallback, useMemo, useState } from 'react';
import { LESSON_THEME_COLORS } from '@course-authoring/shared-types';
import type { ThemeTokens } from '@course-authoring/shared-types';
import { COLOR_KEY, type ColorRowSpec } from './themeSettingsUtils';

export interface ThemeColorsSectionViewModel {
  themeColorSpecs: ColorRowSpec[];
  lessonColorSpecs: ColorRowSpec[];
  colors: Record<string, string>;
  onColorChange: (id: string, value: string) => void;
}

export interface ThemeColorSettingsInternals {
  themeFloor: { sidebar: string; sidebarLogo: string; accent: string };
  colors: Record<string, string>;
  setColors: (colors: Record<string, string>) => void;
  logoBackgroundColor: string;
  certBackgroundColor: string;
  sectionViewModel: ThemeColorsSectionViewModel;
}

export function useThemeColorSettings(tokens: ThemeTokens, disabled = false): ThemeColorSettingsInternals {
  const themeFloor = useMemo(() => ({
    sidebar: tokens.backgrounds.sidebar,
    sidebarLogo: tokens.backgrounds.sidebarLogo,
    accent: tokens.brand.accent ?? tokens.brand.primary,
  }), [tokens.backgrounds.sidebar, tokens.backgrounds.sidebarLogo, tokens.brand.accent, tokens.brand.primary]);

  const themeColorSpecs: ColorRowSpec[] = useMemo(
    () => [
      { id: COLOR_KEY.sidebar, label: 'Left panel background', defaultValue: themeFloor.sidebar },
      { id: COLOR_KEY.sidebarLogo, label: 'Logo background in left panel', defaultValue: themeFloor.sidebarLogo },
      {
        id: COLOR_KEY.accent,
        label: 'Accent color',
        detail: '(buttons, Ema panel & welcome modal headings, and left panel on completion certificate)',
        defaultValue: themeFloor.accent,
      },
    ],
    [themeFloor]
  );

  const lessonColorSpecs: ColorRowSpec[] = useMemo(
    () => [
      { id: COLOR_KEY.lesson1, label: 'Lesson color 1', defaultValue: LESSON_THEME_COLORS.color1 },
      { id: COLOR_KEY.lesson2, label: 'Lesson color 2', defaultValue: LESSON_THEME_COLORS.color2 },
      { id: COLOR_KEY.lesson3, label: 'Lesson color 3', defaultValue: LESSON_THEME_COLORS.color3 },
      { id: COLOR_KEY.lesson4, label: 'Lesson color 4', defaultValue: LESSON_THEME_COLORS.color4 },
    ],
    []
  );

  const floorColors = useMemo<Record<string, string>>(() => {
    const seed: Record<string, string> = {};
    [...themeColorSpecs, ...lessonColorSpecs].forEach((spec) => {
      seed[spec.id] = spec.defaultValue;
    });
    return seed;
  }, [themeColorSpecs, lessonColorSpecs]);

  const [colors, setColors] = useState<Record<string, string>>(floorColors);

  const handleColorChange = useCallback((id: string, value: string) => {
    if (disabled) return;
    setColors((current) => ({ ...current, [id]: value }));
  }, [disabled]);

  const logoBackgroundColor = colors[COLOR_KEY.sidebarLogo] ?? tokens.backgrounds.sidebarLogo;
  const certBackgroundColor = tokens.backgrounds.page;

  return {
    themeFloor,
    colors,
    setColors,
    logoBackgroundColor,
    certBackgroundColor,
    sectionViewModel: {
      themeColorSpecs,
      lessonColorSpecs,
      colors,
      onColorChange: handleColorChange,
    },
  };
}
